import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import useConfigurator from '../state/useConfigurator';
import { PALETTE, SIZES } from '../constants/palette';

const Configurator = () => {
  const { 
    selectedColor, 
    selectedSize, 
    selectedSole, 
    selectedLace,
    setSelectedColor, 
    setSelectedSize,
    setSelectedSole,
    setSelectedLace,
    getConfigJSON 
  } = useConfigurator();
  const [activeTab, setActiveTab] = useState('ALL');
  const [copied, setCopied] = useState(false);
  
  const handleColorSelect = (color) => {
    setSelectedColor(color);
  };
  
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
  };
  
  const handleCopyConfig = () => {
    const config = getConfigJSON();
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    setCopied(true);
    toast({
      title: "Configuration copied!",
      description: "Your shoe configuration has been copied to clipboard.",
    });
    setTimeout(() => setCopied(false), 2000);
  };
  
  const colors = PALETTE[activeTab] || [];
  
  return (
    <div className="w-full space-y-4">
      {/* Title Section */}
      <div className="space-y-1 animate-slideUp">
        <h2 className="text-xl font-semibold text-gray-900">Customize Your Shoe</h2>
        <p className="text-xs text-gray-600">Choose your perfect combination</p>
      </div>
      
      {/* Tabs */}
      <div className="flex items-center justify-center gap-6 border-b border-gray-200 pb-1 animate-slideUp" style={{animationDelay: '0.1s'}}>
        {['ALL', 'LIMITED', 'CLASSIC'].map((tab, index) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={
              `text-xs font-semibold pb-2 transition-all duration-300 relative group
              ${activeTab === tab 
                ? 'text-gray-900' 
                : 'text-gray-500 hover:text-gray-700'
              }`
            }
            style={{animationDelay: `${0.1 + index * 0.05}s`}}
            aria-label={`Show ${tab.toLowerCase()} colors`}
          >
            {tab}
            {activeTab === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-gray-900 to-gray-600 rounded-full transform transition-all duration-300" />
            )}
            {activeTab !== tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-300 scale-x-0 group-hover:scale-x-100 rounded-full transform transition-transform duration-300" />
            )}
          </button>
        ))}
      </div>
      
      {/* Color Swatches */}
      <div className="grid grid-cols-7 gap-2">
        {colors.map((color, index) => (
          <button
            key={color.name}
            onClick={() => handleColorSelect(color)}
            className={
              `group relative w-full aspect-square rounded-full transition-all duration-200 ease-out animate-slideUp
              ${selectedColor.hex === color.hex 
                ? 'ring-2 ring-offset-2 ring-gray-900 scale-105 shadow-lg' 
                : 'hover:scale-103 shadow-sm hover:shadow-md'
              }`
            }
            style={{ 
              backgroundColor: color.hex,
              animationDelay: `${0.2 + index * 0.03}s`
            }}
            aria-label={`Select ${color.name} color`}
          >
            <span className="sr-only">{color.name}</span>
            {selectedColor.hex === color.hex && (
              <div className="absolute inset-0 flex items-center justify-center animate-fadeIn">
                <Check className="w-5 h-5 text-white drop-shadow-2xl" strokeWidth={3} />
              </div>
            )}
            {/* Subtle hover glow */}
            <div className="absolute inset-0 rounded-full bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          </button>
        ))}
      </div>
      
      {/* Selected Color Name */}
      <div className="text-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl transform transition-all duration-300 hover:shadow-md animate-slideUp" style={{animationDelay: '0.3s'}}>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Selected Color</p>
        <p className="text-base font-semibold text-gray-900 mt-1 transition-all duration-300">{selectedColor.name}</p>
      </div>
      
      {/* Sole & Lace Customization */}
      <div className="grid grid-cols-2 gap-4 animate-slideUp" style={{animationDelay: '0.4s'}}>
        {/* Sole Selection */}
        <div className="space-y-2">
          <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider block">
            Sole Color
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedSole('white')}
              className={`flex-1 py-2.5 px-3 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 ${
                selectedSole === 'white'
                  ? 'border-gray-900 bg-gradient-to-br from-gray-50 to-white shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              aria-label="Select white sole"
            >
              <div className="flex items-center justify-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-white border-2 border-gray-300 shadow-sm" />
                <span className="text-xs font-semibold text-gray-900">White</span>
              </div>
            </button>
            <button
              onClick={() => setSelectedSole('black')}
              className={`flex-1 py-2.5 px-3 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 ${
                selectedSole === 'black'
                  ? 'border-gray-900 bg-gradient-to-br from-gray-50 to-white shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              aria-label="Select black sole"
            >
              <div className="flex items-center justify-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-black border-2 border-gray-300 shadow-sm" />
                <span className="text-xs font-semibold text-gray-900">Black</span>
              </div>
            </button>
          </div>
        </div>
        
        {/* Lace Selection */}
        <div className="space-y-2">
          <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider block">
            Lace Color
          </label>
          <div className="flex gap-1.5">
            <button
              onClick={() => setSelectedLace('same')}
              className={`flex-1 py-2.5 px-2 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 ${
                selectedLace === 'same'
                  ? 'border-gray-900 bg-gradient-to-br from-gray-50 to-white shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              aria-label="Match lace to body color"
            >
              <span className="text-xs font-semibold text-gray-900">Match</span>
            </button>
            <button
              onClick={() => setSelectedLace('white')}
              className={`flex-1 py-2.5 px-2 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 ${
                selectedLace === 'white'
                  ? 'border-gray-900 bg-gradient-to-br from-gray-50 to-white shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              aria-label="Select white laces"
            >
              <div className="flex items-center justify-center gap-1">
                <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-300 shadow-sm" />
                <span className="text-[10px] font-semibold text-gray-900">White</span>
              </div>
            </button>
            <button
              onClick={() => setSelectedLace('black')}
              className={`flex-1 py-2.5 px-2 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 ${
                selectedLace === 'black'
                  ? 'border-gray-900 bg-gradient-to-br from-gray-50 to-white shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              aria-label="Select black laces"
            >
              <div className="flex items-center justify-center gap-1">
                <div className="w-4 h-4 rounded-full bg-black border-2 border-gray-300 shadow-sm" />
                <span className="text-[10px] font-semibold text-gray-900">Black</span>
              </div>
            </button>
          </div>
        </div>
      </div>
      
      {/* Size Selection */}
      <div className="flex items-center gap-3 bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 shadow-lg border border-gray-200 animate-slideUp" style={{animationDelay: '0.5s'}}>
        <div className="flex-1">
          <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">
            Size
          </label>
          <Select value={selectedSize || ''} onValueChange={handleSizeSelect}>
            <SelectTrigger 
              className="w-full h-10 rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-gray-900 transition-all duration-300 hover:border-gray-400 bg-white text-sm"
              aria-label="Select shoe size"
            >
              <SelectValue placeholder="Select a size" />
            </SelectTrigger>
            <SelectContent>
              {SIZES.map((size) => (
                <SelectItem key={size} value={size}>
                  US {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button
          size="lg"
          disabled={!selectedSize}
          className={
            `flex-1 h-10 rounded-lg font-semibold transition-all duration-300 transform text-xs
            ${selectedSize 
              ? 'bg-gradient-to-r from-gray-900 to-gray-700 hover:from-gray-800 hover:to-gray-600 text-white shadow-lg hover:shadow-xl hover:scale-105' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`
          }
          aria-label="Select size to continue"
        >
          SELECT A SIZE
        </Button>
      </div>
      
      {/* Copy Config */}
      <div className="flex justify-center animate-slideUp" style={{animationDelay: '0.6s'}}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyConfig}
          className="rounded-full border-2 border-gray-300 hover:border-gray-900 text-gray-700 gap-2 transition-all duration-300 hover:shadow-md transform hover:scale-105 text-xs py-1.5"
          aria-label="Copy configuration to clipboard"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 animate-fadeIn" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              Copy Config
            </>
          )}
        </Button>
      </div>
      
      {/* Microcopy */}
      <div className="text-center space-y-2 pt-3 border-t border-gray-200 animate-slideUp" style={{animationDelay: '0.7s'}}>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
          <p className="text-xs text-green-900 font-medium">
            Free Shipping on Orders Over $75
          </p>
        </div>
        <p className="text-xs text-gray-600">
          ✨ Easy Returns • Sustainable Materials
        </p>
      </div>
    </div>
  );
};

export default Configurator;
